import { getApiUrl } from './apiConfig';
import { getRefresh, getToken, setRefresh, setToken, setRole, clearAuth, AppRole } from './role';

// Cross-tab-safe session refresh.
//
// The server ROTATES the refresh token on every exchange and revokes the previous one. With several tabs
// open (report / ecosystem / portfolio open in new windows) two tabs could exchange the SAME refresh token
// at once: the second got 401 and called clearAuth(), wiping the tokens of EVERY tab — gated endpoints then
// answered 402 "requires Pro" to a paying subscriber. Now:
//   1. refreshes are serialized across tabs (Web Locks API when available, else a short localStorage lease);
//   2. inside the lock the refresh token is RE-READ — if another tab already rotated it, we use the result;
//   3. a failed exchange clears auth ONLY if nobody rotated the token meanwhile (a genuine dead session).
const LEASE_KEY = 'saa_refresh_lease';
let inflight: Promise<boolean> | null = null;

async function doExchange(rtAtStart: string | null): Promise<boolean> {
  const rt = getRefresh();
  if (!rt) return false;
  if (rtAtStart && rt !== rtAtStart) return true; // another tab rotated while we waited — its token is ours
  try {
    const res = await fetch(getApiUrl('/api/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) {
      if (getRefresh() === rt) clearAuth(); // only a genuinely dead session is cleared
      return getRefresh() !== rt && !!getRefresh();
    }
    const d = await res.json();
    if (d.token) setToken(d.token);
    if (d.refresh_token) setRefresh(d.refresh_token);
    if (d.role) setRole(d.role as AppRole);
    return true;
  } catch {
    return false;
  }
}

async function withLease(fn: () => Promise<boolean>): Promise<boolean> {
  const nav = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: { request: (n: string, cb: () => Promise<boolean>) => Promise<boolean> } }) : undefined;
  if (nav?.locks?.request) return nav.locks.request('saa-auth-refresh', fn);
  // Fallback lease: wait (≤3s) while another tab holds a fresh lease.
  for (let i = 0; i < 30; i++) {
    const held = Number(window.localStorage.getItem(LEASE_KEY) || 0);
    if (!held || Date.now() - held > 5000) break;
    await new Promise((r) => setTimeout(r, 100));
  }
  try { window.localStorage.setItem(LEASE_KEY, String(Date.now())); } catch { /* ignore */ }
  try { return await fn(); } finally { try { window.localStorage.removeItem(LEASE_KEY); } catch { /* ignore */ } }
}

/** Exchange the refresh token for a fresh access token; single-flight per tab, serialized across tabs. */
export function refreshSession(): Promise<boolean> {
  if (inflight) return inflight;
  const rtAtStart = getRefresh();
  inflight = withLease(() => doExchange(rtAtStart)).finally(() => { inflight = null; });
  return inflight;
}

/** Seconds until the stored access token expires (its JWT exp); 0 when absent or unreadable. */
export function accessTokenSecondsLeft(): number {
  const t = getToken();
  if (!t) return 0;
  try {
    const part = t.split('.')[1] || '';
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=')));
    const exp = Number(json?.exp || 0);
    return exp > 0 ? Math.max(0, exp - Math.floor(Date.now() / 1000)) : 0;
  } catch {
    return 0;
  }
}

// Renew only when the access token is missing or close to expiry. A new tab / window used to exchange the
// refresh token unconditionally on load — every Ecosystem / Detailed Analysis / report window rotated the
// session while the other windows' requests were still in flight (FET incident 2026-09-25).
export const RENEW_BEFORE_S = 20 * 60;
export function ensureFreshSession(minSecondsLeft: number = RENEW_BEFORE_S): Promise<boolean> {
  if (accessTokenSecondsLeft() > minSecondsLeft) return Promise.resolve(true);
  if (!getRefresh()) return Promise.resolve(false);
  return refreshSession();
}
